import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, FileText, Ruler, Wrench, CheckSquare } from 'lucide-react';

const steps = [
  {
    icon: MessageSquare,
    number: '01',
    title: 'Beratung',
    description: 'Persönliches Gespräch, um Ihre Wünsche und Anforderungen genau zu verstehen.',
  },
  {
    icon: FileText,
    number: '02',
    title: 'Angebot',
    description: 'Sie erhalten ein faires, nachvollziehbares Angebot – ehrlich kalkuliert, ohne Kleingedrucktes.',
  },
  {
    icon: Ruler,
    number: '03',
    title: 'Planung',
    description: 'Detaillierte Planung aller Installationen, abgestimmt auf Ihre Gegebenheiten.',
  },
  {
    icon: Wrench,
    number: '04',
    title: 'Ausführung',
    description: 'Fachgerechte Umsetzung durch qualifizierte Elektriker – sauber und termingerecht.',
  },
  {
    icon: CheckSquare,
    number: '05',
    title: 'Abnahme',
    description: 'Gemeinsame Abnahme und Einweisung – erst wenn Sie zufrieden sind, sind wir es auch.',
  },
];

export default function ProzessSection() {
  return (
    <section id="prozess" data-testid="prozess-section" className="py-24 sm:py-32 bg-[#1A3A22] relative overflow-hidden noise-overlay">
      {/* Decorative circles */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#2D5A27]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#10B981]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Unser Prozess
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-white tracking-tight mt-3 mb-4">
            In 5 Schritten zum <span className="text-[#10B981]">Ergebnis</span>
          </h2>
          <p className="font-['Manrope'] text-white/60 text-base sm:text-lg max-w-xl mx-auto">
            Transparenz und Sicherheit in jedem Schritt – von der ersten Beratung bis zur Abnahme.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              data-testid={`process-step-${idx}`}
              className="relative group"
            >
              {/* Connector line for desktop */}
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+28px)] right-0 h-[2px] bg-gradient-to-r from-[#2D5A27] to-[#10B981]/30 z-0" />
              )}

              <div className="relative z-10 text-center p-6">
                <div className="w-16 h-16 mx-auto bg-[#2D5A27]/40 border border-[#10B981]/20 rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#10B981]/20 transition-colors duration-300">
                  <step.icon className="w-8 h-8 text-[#10B981]" strokeWidth={1.5} />
                </div>
                <span className="font-['Outfit'] text-[#10B981]/50 text-3xl font-extrabold block mb-2">
                  {step.number}
                </span>
                <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="font-['Manrope'] text-white/50 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
